import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight, Calendar, User, Tag } from "lucide-react";
import PageBanner from "../components/PageBanner";
import { blogPageData } from "../data/pageData";

const BlogPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const categories = ["All", ...new Set(blogPageData.posts.map(p => p.category))];
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = activeCategory === "All"
    ? blogPageData.posts
    : blogPageData.posts.filter(p => p.category === activeCategory);

  return (
    <>
      <PageBanner
        title="Blog & Insights"
        subtitle="Latest News from the World of 3D Printing"
        image={blogPageData.heroImage}
        breadcrumbs={[{ label: "Blog" }]}
      />

      <section className="inner-section" ref={ref}>
        <div className="inner-container">
          <div className="project-filters">
            {categories.map(cat => (
              <button key={cat}
                className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}>{cat}</button>
            ))}
          </div>

          <div className="blog-page-grid">
            {filtered.map((post, i) => (
              <motion.div key={post.id} className="blog-page-card"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.08 }}>
                <div className="bpc-image">
                  <img src={post.image} alt={post.title} loading="lazy" />
                  <div className="blog-date">
                    <span className="blog-day">{post.date}</span>
                    <span className="blog-month">{post.month}</span>
                    <span className="blog-year">{post.year}</span>
                  </div>
                </div>
                <div className="bpc-body">
                  <div className="bpc-meta">
                    <span><User size={14} /> {post.author}</span>
                    <span><Tag size={14} /> {post.category}</span>
                  </div>
                  <h3><Link to={`/blog/${post.id}`}>{post.title}</Link></h3>
                  <p>{post.excerpt}</p>
                  <Link to={`/blog/${post.id}`} className="btn-dark-sm">Read More</Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export const BlogDetailPage = () => {
  const { id } = useParams();
  const post = blogPageData.posts.find(p => p.id === parseInt(id));
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  if (!post) {
    return (
      <div className="inner-section" style={{ textAlign: 'center', padding: '120px 24px' }}>
        <h2>Post Not Found</h2>
        <Link to="/blog" className="btn-primary" style={{ marginTop: 24, display: 'inline-flex' }}>Back to Blog</Link>
      </div>
    );
  }

  const relatedPosts = blogPageData.posts.filter(p => p.id !== post.id).slice(0, 3);

  return (
    <>
      <PageBanner
        title={post.title}
        subtitle={`${post.date} ${post.month} ${post.year} — By ${post.author}`}
        image={post.image}
        breadcrumbs={[
          { label: "Blog", link: "/blog" },
          { label: post.title.length > 40 ? post.title.substring(0, 40) + '...' : post.title }
        ]}
      />

      <section className="inner-section" ref={ref}>
        <div className="inner-container">
          <motion.div className="blog-detail-layout" initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
            <article className="blog-detail-content">
              <div className="bdc-meta">
                <span><Calendar size={14} /> {post.date} {post.month} {post.year}</span>
                <span><User size={14} /> {post.author}</span>
                <span><Tag size={14} /> {post.category}</span>
              </div>
              <img src={post.image} alt={post.title} className="bdc-hero-img" loading="lazy" />
              <div className="bdc-body">
                <p className="bdc-excerpt">{post.excerpt}</p>
                <p>{post.content}</p>
                <h3>Why This Matters for Bangalore's Manufacturing Ecosystem</h3>
                <p>
                  Bangalore, as India's technology capital, is at the forefront of adopting advanced manufacturing
                  technologies. 3D printing services in Bangalore are enabling startups and enterprises to innovate
                  faster, reduce costs, and bring products to market in record time. At 3D Printzkart, we're proud
                  to be driving this transformation with our comprehensive suite of additive manufacturing services.
                </p>
                <h3>Key Takeaways</h3>
                <ul>
                  <li>3D printing accelerates product development cycles by 60-80%</li>
                  <li>Costs for small batch production are significantly lower than traditional manufacturing</li>
                  <li>Design freedom enables complex geometries impossible with conventional methods</li>
                  <li>Multiple material options cater to diverse industry requirements</li>
                </ul>
                <p>
                  Whether you're a startup building your first prototype or an enterprise scaling production,
                  3D Printzkart in Bangalore has the technology, expertise, and capacity to support your journey.
                  Contact us today for a free consultation.
                </p>
              </div>
            </article>
            <aside className="blog-detail-sidebar">
              <div className="bds-card">
                <h4>Related Posts</h4>
                {relatedPosts.map(rp => (
                  <Link key={rp.id} to={`/blog/${rp.id}`} className="bds-related">
                    <img src={rp.image} alt={rp.title} />
                    <div>
                      <span className="bds-date">{rp.date} {rp.month} {rp.year}</span>
                      <h5>{rp.title}</h5>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="bds-card accent">
                <h4>Need 3D Printing?</h4>
                <p>Get a free quote for your project from Bangalore's best 3D printing service.</p>
                <Link to="/contact" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                  Get Free Quote <ArrowRight size={16} />
                </Link>
              </div>
            </aside>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default BlogPage;
